import React, { useMemo } from 'react';
import { TimePeriod } from 'common/types';
import { MINUTES, HOURS, DAYS, DAYS_24H, WEEKS, MONTHS } from 'common/constants/timePeriods';

import { TextField, MenuItem, TextFieldProps } from '@mui/material';
import { useTranslation } from 'services/localization/useTranslation';

interface Props {
	value: TimePeriod | undefined;
	onChange: (value: TimePeriod | undefined) => void;
	labelFormat: 'plural' | 'singular' | 'per';
	noneLabel?: string;
	disableNone?: boolean;
	textFieldProps?: TextFieldProps;
}

type OutputValue = TimePeriod | undefined;

const NONE = '__none__';

const TimePeriodSelect = (props: Props) => {
	const { value, onChange, labelFormat, noneLabel, disableNone } = props;
	const { t } = useTranslation();

	const handleChange = (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
		const outputValue = e.target.value !== NONE ? e.target.value : undefined;
		onChange(outputValue as OutputValue);
	};

	const options = useMemo(() => {
		switch (labelFormat) {
			case 'plural': {
				return [
					{
						value: MINUTES,
						label: t('common:times.minutes'),
					},
					{
						value: HOURS,
						label: t('common:times.hours'),
					},
					{
						value: DAYS,
						label: t('common:times.days_long', 'days (within opening hours)'),
					},
					{
						value: DAYS_24H,
						label: t('common:times.days24'),
					},
					{
						value: WEEKS,
						label: t('common:times.weeks'),
					},
					{
						value: MONTHS,
						label: t('common:times.months'),
					},
				];
			}
			case 'singular': {
				return [
					{
						value: MINUTES,
						label: t('common:times.minutes'),
					},
					{
						value: HOURS,
						label: t('common:times.hour'),
					},
					{
						value: DAYS,
						label: t('common:times.day_long_singular', 'day (within opening hours)'),
					},
					{
						value: DAYS_24H,
						label: t('common:times.days24_singular', 'day (24h)'),
					},
					{
						value: WEEKS,
						label: t('common:times.week', 'week'),
					},
					{
						value: MONTHS,
						label: t('common:times.month', 'month'),
					},
				];
			}
			case 'per': {
				return [
					{
						value: MINUTES,
						label: t('product.per_time', {
							time: t('common:times.minute', 'minute'),
							defaultValue: 'per {{time}}',
						}),
					},
					{
						value: HOURS,
						label: t('product.per_time', {
							time: t('common:times.hour'),
							defaultValue: 'per {{time}}',
						}),
					},
					{
						value: DAYS,
						label: t('product.per_time', {
							time: t('common:times.day_long'),
							defaultValue: 'per {{time}}',
						}),
					},
					{
						value: DAYS_24H,
						label: t('product.per_time', {
							time: t('common:times.days24_singular'),
							defaultValue: 'per {{time}}',
						}),
					},
					{
						value: WEEKS,
						label: t('product.per_time', {
							time: t('common:times.week'),
							defaultValue: 'per {{time}}',
						}),
					},
					{
						value: MONTHS,
						label: t('product.per_time', {
							time: t('common:times.month', 'month'),
							defaultValue: 'per {{time}}',
						}),
					},
				];
			}
			default:
				return [];
		}
	}, [labelFormat, t]);

	return (
		<TextField
			select
			value={value ?? NONE}
			onChange={handleChange}
			fullWidth
			{...props.textFieldProps}
		>
			{!disableNone && (
				<MenuItem key={NONE} value={NONE}>
					{noneLabel ?? '-'}
				</MenuItem>
			)}

			{options.map((option) => (
				<MenuItem key={option.value} value={option.value}>
					{option.label.toLowerCase()}
				</MenuItem>
			))}
		</TextField>
	);
};

export default TimePeriodSelect;
